import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Link,
  VStack
} from '@chakra-ui/react';
import { theme as mythboundTheme } from '../../config/theme';

/**
 * Parent-owned controls for the server-backed policy gate. This view receives
 * eligibility and request state rather than deciding acceptance locally;
 * only a confirmed, matching server status can close the gate.
 */
interface LegalAcceptanceModalProps {
  isOpen: boolean;
  onAgree: () => void;
  onDecline: () => void;
  onRetry: () => void;
  canAccept: boolean;
  isSubmitting: boolean;
  statusMessage?: string | null;
  errorMessage?: string | null;
}

export const LegalAcceptanceModal: React.FC<LegalAcceptanceModalProps> = ({
  isOpen,
  onAgree,
  onDecline,
  onRetry,
  canAccept,
  isSubmitting,
  statusMessage,
  errorMessage,
}) => {
  // Keep this modal non-dismissible: the parent must complete or decline the
  // policy flow explicitly, and unavailable status never counts as consent.
  return (
    <Modal isOpen={isOpen} onClose={() => {}} isCentered size="xl" closeOnOverlayClick={false} closeOnEsc={false}>
      <ModalOverlay bg="rgba(5,10,20,0.85)" />
      <ModalContent
        bg={mythboundTheme.colors.secondary.parchment || '#f2e4c7'}
        border="1px solid rgba(0,0,0,0.12)"
        boxShadow={mythboundTheme.shadows?.glow || '0 0 32px rgba(0,0,0,0.45)'}
        fontFamily={mythboundTheme.typography.fontFamily.body}
      >
        <ModalHeader
          fontFamily={mythboundTheme.typography.fontFamily.heading}
          color={mythboundTheme.colors.primary?.gold || '#d9b555'}
        >
          Please review before continuing
        </ModalHeader>
        <ModalBody>
          <VStack align="start" spacing={3}>
            <Text color={mythboundTheme.colors.primary.dark}>
              To continue using Mythbound™, please review and agree to the Terms of Service and Privacy Policy.
              These links open the full text on parchment with our standard layout.
            </Text>
            <Link href="/terms" color={mythboundTheme.colors.primary.main} textDecoration="underline">
              Terms of Service
            </Link>
            <Link href="/privacy" color={mythboundTheme.colors.primary.main} textDecoration="underline">
              Privacy Policy
            </Link>
            {statusMessage && <Text role="status" color={mythboundTheme.colors.primary.dark}>{statusMessage}</Text>}
            {errorMessage && <Text role="alert" color="red.700">{errorMessage}</Text>}
            {/* A retry refreshes server eligibility; it does not accept either policy. */}
            {!canAccept && (
              <Button onClick={onRetry} isDisabled={isSubmitting} variant="outline">
                Try again
              </Button>
            )}
            <Text fontSize="sm" color="rgba(0,0,0,0.7)">
              You can decline and log out if you do not agree. You may be prompted again if the terms change.
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            mr={3}
            bg={mythboundTheme.colors.primary?.main || '#3be8ff'}
            color={mythboundTheme.colors.primary?.dark || '#111116'}
            _hover={{ bg: mythboundTheme.colors.primary?.main }}
            onClick={onAgree}
            isDisabled={!canAccept || isSubmitting}
            isLoading={isSubmitting}
          >
            Agree to Use Mythbound™
          </Button>
          <Button variant="outline" colorScheme="red" onClick={onDecline} isDisabled={isSubmitting}>
            Decline and Logout
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LegalAcceptanceModal;
